import { WMM_COEFFS, WMM_EPOCH } from './wmm2025';

const N = 12;
const A_REF = 6371.2; // km, geomagnetic reference radius
const WGS_A = 6378.137;
const WGS_F = 1 / 298.257223563;
const WGS_E2 = WGS_F * (2 - WGS_F);

const G = Array.from({ length: N + 1 }, () => new Float64Array(N + 1));
const H = Array.from({ length: N + 1 }, () => new Float64Array(N + 1));
const GD = Array.from({ length: N + 1 }, () => new Float64Array(N + 1));
const HD = Array.from({ length: N + 1 }, () => new Float64Array(N + 1));
for (const [n, m, g, h, gd, hd] of WMM_COEFFS) {
  G[n][m] = g;
  H[n][m] = h;
  GD[n][m] = gd;
  HD[n][m] = hd;
}

export interface MagneticField {
  declination: number; // degrees, east positive
  inclination: number; // degrees, down positive
  total: number; // nT
}

/**
 * World Magnetic Model 2025 main field at a geodetic position.
 * @param lat geodetic latitude, degrees
 * @param lon longitude, degrees east
 * @param altKm height above the WGS84 ellipsoid, km
 * @param year decimal year
 */
export function magneticField(lat: number, lon: number, altKm: number, year: number): MagneticField {
  const dt = year - WMM_EPOCH;
  const phi = (lat * Math.PI) / 180;
  const lam = (lon * Math.PI) / 180;

  // Geodetic → geocentric spherical.
  const sinPhi = Math.sin(phi), cosPhi = Math.cos(phi);
  const rc = WGS_A / Math.sqrt(1 - WGS_E2 * sinPhi * sinPhi);
  const p = (rc + altKm) * cosPhi;
  const z = (rc * (1 - WGS_E2) + altKm) * sinPhi;
  const r = Math.hypot(p, z);
  const phic = Math.asin(z / r);

  // Schmidt semi-normalised Legendre functions of colatitude θ, and dP/dθ.
  const theta = Math.PI / 2 - phic;
  const ct = Math.cos(theta), st = Math.sin(theta);
  const P = Array.from({ length: N + 1 }, () => new Float64Array(N + 1));
  const dP = Array.from({ length: N + 1 }, () => new Float64Array(N + 1));
  P[0][0] = 1;
  dP[0][0] = 0;
  for (let n = 1; n <= N; n++) {
    for (let m = 0; m <= n; m++) {
      if (n === m) {
        const k = n === 1 ? 1 : Math.sqrt((2 * n - 1) / (2 * n));
        P[n][n] = k * st * P[n - 1][n - 1];
        dP[n][n] = k * (ct * P[n - 1][n - 1] + st * dP[n - 1][n - 1]);
      } else {
        const k1 = 2 * n - 1;
        const k2 = n >= 2 && m <= n - 2 ? Math.sqrt((n - 1) * (n - 1) - m * m) : 0;
        const den = Math.sqrt(n * n - m * m);
        const pm2 = n >= 2 && m <= n - 2 ? P[n - 2][m] : 0;
        const dpm2 = n >= 2 && m <= n - 2 ? dP[n - 2][m] : 0;
        P[n][m] = (k1 * ct * P[n - 1][m] - k2 * pm2) / den;
        dP[n][m] = (k1 * (-st * P[n - 1][m] + ct * dP[n - 1][m]) - k2 * dpm2) / den;
      }
    }
  }

  let Xp = 0, Yp = 0, Zp = 0;
  for (let n = 1; n <= N; n++) {
    const ar = Math.pow(A_REF / r, n + 2);
    for (let m = 0; m <= n; m++) {
      const g = G[n][m] + dt * GD[n][m];
      const h = H[n][m] + dt * HD[n][m];
      const cm = Math.cos(m * lam), sm = Math.sin(m * lam);
      const gc = g * cm + h * sm;
      Xp += ar * gc * dP[n][m]; // −dP/dφ = +dP/dθ
      Yp += ar * m * (g * sm - h * cm) * P[n][m];
      Zp -= ar * (n + 1) * gc * P[n][m];
    }
  }
  const cphic = Math.cos(phic);
  Yp = cphic > 1e-10 ? Yp / cphic : Yp;

  const psi = phic - phi;
  const X = Xp * Math.cos(psi) - Zp * Math.sin(psi);
  const Y = Yp;
  const Z = Xp * Math.sin(psi) + Zp * Math.cos(psi);
  const Hh = Math.hypot(X, Y);
  return {
    declination: (Math.atan2(Y, X) * 180) / Math.PI,
    inclination: (Math.atan2(Z, Hh) * 180) / Math.PI,
    total: Math.hypot(Hh, Z),
  };
}

export function decimalYear(d: Date): number {
  const y = d.getUTCFullYear();
  const start = Date.UTC(y, 0, 1), end = Date.UTC(y + 1, 0, 1);
  return y + (d.getTime() - start) / (end - start);
}

/** Magnetic declination (degrees east of true north) at a place and time. */
export function declination(lat: number, lon: number, heightM = 0, date = new Date()): number {
  return magneticField(lat, lon, heightM / 1000, decimalYear(date)).declination;
}
